import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Terminal, Lock, Package, MessageSquare, Lightbulb, Sparkles } from "lucide-react";

export default function CLIIntegrationTab() {
  return (
    <div className="space-y-6">
      {/* LocalDocs CLI Commands */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            LocalDocs CLI Commands
          </CardTitle>
          <CardDescription>Full-featured CLI for interacting with LocalDocs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Search Team Documentation</h4>
            <code className="block bg-muted p-2 rounded text-sm font-mono">
              npx @projectassistant/localdocs search "React"
            </code>
            <p className="text-muted-foreground text-sm mt-1">
              Search through your team's documentation library
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Export Filtered Documentation</h4>
            <code className="block bg-muted p-2 rounded text-sm font-mono">
              npx @projectassistant/localdocs export --tags react --output ./docs
            </code>
            <p className="text-muted-foreground text-sm mt-1">
              Export documentation filtered by tags to a local directory
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Getting the CLI */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Getting the LocalDocs CLI
          </CardTitle>
          <CardDescription>Published npm package - use anywhere with npx</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p className="text-sm font-semibold mb-2">🚀 Quick Start (Recommended)</p>
            <p className="text-sm text-muted-foreground mb-2">
              Use the published npm package directly with npx - no installation needed:
            </p>
            <code className="block bg-muted p-2 rounded text-sm font-mono">
              npx @projectassistant/localdocs login
            </code>
            <p className="text-xs text-muted-foreground mt-2">
              ✅ Works from any directory • ✅ Always uses latest version • ✅ No setup required
            </p>
          </div>

          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
            <p className="text-sm font-semibold mb-2">📦 Global Installation (Optional)</p>
            <p className="text-sm text-muted-foreground mb-2">
              Install globally to use shorter commands:
            </p>
            <code className="block bg-muted p-2 rounded text-sm font-mono mb-2">
              npm install -g @projectassistant/localdocs
            </code>
            <p className="text-sm text-muted-foreground mb-1">Then use:</p>
            <code className="block bg-muted p-2 rounded text-sm font-mono">
              localdocs search "React"
            </code>
          </div>

          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800">
            <p className="text-sm font-semibold mb-2">🔧 Template Projects (Legacy)</p>
            <p className="text-sm text-muted-foreground mb-2">
              For projects using the Context Engineering Template Enhanced, the CLI is already
              included in the <code className="bg-muted px-1 rounded">cli/</code> directory.
            </p>
            <p className="text-xs text-muted-foreground">
              💡 Tip: Consider migrating to the npm package for easier updates
            </p>
          </div>
        </CardContent>
      </Card>

      {/* MCP Integration for Claude Code */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            MCP Integration for Claude Code
          </CardTitle>
          <CardDescription>
            Use LocalDocs directly in Claude Code with natural language
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Installation</h4>
            <code className="block bg-muted p-2 rounded text-sm font-mono">
              claude mcp add localdocs -- npx -y -p @projectassistant/localdocs localdocs-mcp
            </code>
            <p className="text-muted-foreground text-sm mt-1">
              Install LocalDocs as an MCP server for Claude Code
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Natural Language Usage</h4>
            <div className="space-y-2">
              <div className="bg-primary/5 p-2 rounded">
                <p className="text-sm italic">"Search our LocalDocs for React documentation"</p>
              </div>
              <div className="bg-primary/5 p-2 rounded">
                <p className="text-sm italic">"Export our FastAPI docs to this project"</p>
              </div>
              <div className="bg-primary/5 p-2 rounded">
                <p className="text-sm italic">"Add the Supabase docs to our team library"</p>
              </div>
            </div>
            <p className="text-muted-foreground text-sm mt-2">
              Claude Code automatically uses LocalDocs MCP tools - no need to type commands!
            </p>
          </div>

          <div className="p-3 bg-green-500/10 rounded-md border border-green-500/20">
            <p className="font-semibold text-green-700 dark:text-green-400">✨ One-Time Setup</p>
            <p className="text-muted-foreground text-sm mt-1">
              Authenticate once with{" "}
              <code className="bg-muted px-1 rounded">npx @projectassistant/localdocs login</code>.
              Both CLI and MCP use the same 30-day authentication.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* OAuth Device Flow Authentication */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Authenticate via OAuth Device Flow
          </CardTitle>
          <CardDescription>
            One-time authentication - no manual token extraction required!
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="font-semibold mb-1">Step 1: Start Device Flow</p>
            <code className="block bg-muted p-2 rounded text-sm font-mono">
              npx @projectassistant/localdocs login
            </code>
            <p className="text-sm text-muted-foreground mt-1">
              CLI displays a 6-character code (e.g., ABC-123) and /cli-auth URL
            </p>
          </div>

          <div>
            <p className="font-semibold mb-1">Step 2: Approve in Browser</p>
            <p className="text-sm text-muted-foreground">
              Visit <code className="bg-muted px-1 rounded">/cli-auth</code>, enter your code, click
              "Approve"
            </p>
          </div>

          <div>
            <p className="font-semibold mb-1">Step 3: Done!</p>
            <p className="text-sm text-muted-foreground">
              Access token saved to{" "}
              <code className="bg-muted px-1 rounded">~/.localdocs-cli/config.json</code>
              <br />
              Valid for 30 days - no weekly re-auth needed!
            </p>
          </div>

          <div className="mt-4 p-3 bg-primary/5 rounded-md border border-primary/20">
            <p className="font-semibold mb-2">Security Features:</p>
            <ul className="text-muted-foreground text-sm space-y-1 ml-4 list-disc">
              <li>30-day access tokens with automatic expiration</li>
              <li>Immediate revocation when users removed from team</li>
              <li>User status checked on every request</li>
              <li>SHA-256 token hashing in database</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Using with Claude Code (MCP vs Direct CLI) */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Two Ways to Use with Claude Code
          </CardTitle>
          <CardDescription>
            Choose between MCP Server (recommended) or direct CLI usage
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <p className="font-semibold text-sm mb-2">🌟 Option 1: MCP Server (Recommended)</p>
            <p className="text-sm text-muted-foreground mb-2">
              Install the MCP server once, then use natural language:
            </p>
            <div className="space-y-2">
              <div className="bg-primary/5 p-2 rounded">
                <p className="text-xs italic">"Search our LocalDocs for React documentation"</p>
              </div>
              <div className="bg-primary/5 p-2 rounded">
                <p className="text-xs italic">"Export our FastAPI docs to this project"</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              ✅ No slash commands needed • ✅ Automatic tool detection • ✅ Most convenient
            </p>
          </div>

          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800">
            <p className="font-semibold text-sm mb-2">
              🔧 Option 2: Direct CLI (For Template Projects)
            </p>
            <p className="text-sm text-muted-foreground mb-2">
              If you copied the CLI to your project, Claude can invoke it directly:
            </p>
            <div className="space-y-2">
              <div className="text-xs">
                <p className="font-semibold mb-1">When you say:</p>
                <p className="italic text-muted-foreground">"Search our LocalDocs for FastAPI"</p>
                <p className="font-semibold mb-1 mt-2">Claude runs:</p>
                <code className="bg-muted px-1 rounded">
                  node cli/localdocs-cli.js search "FastAPI"
                </code>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              ⚠️ Requires CLI in your project directory • Useful for template-based projects
            </p>
          </div>

          <div className="p-3 bg-green-500/10 rounded-md border border-green-500/20">
            <p className="font-semibold text-green-700 dark:text-green-400">💡 Which to Use?</p>
            <p className="text-muted-foreground text-sm mt-1">
              <strong>Use MCP</strong> for most cases - it's cleaner and works across all projects.
              <br />
              <strong>Use Direct CLI</strong> only if you've already integrated it into your project
              template.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Pro Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Pro Tips
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="p-3 bg-primary/5 rounded-md">
            <p className="text-sm">
              💡 <strong>Pro Tip:</strong> Authenticate once via OAuth, then access your team's
              documentation from any project
            </p>
          </div>
          <div className="p-3 bg-primary/5 rounded-md">
            <p className="text-sm">
              📦 Works in all projects using our Context Engineering Template Enhanced boilerplate
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
